<template>
  <div class="team-wrapper" @mouseover="toggleDropdown" @mouseleave="pauseOut">
    <!-- Static Team View -->
    <div class="team-static-container cursor-pointer" :class="{'team-private-edit' : members.length < 2 && editMode}">
      <div class="team-static is-flex">
        <div class="all-team is-flex">
          <div class="member" :style="{ 'background-image': 'url(' + avatar + ')'}" data-balloon="Owner" data-balloon-pos="up"></div>
          <div v-for="member in nonOwners" :key="member.user.userName" class="member" v-bind:style="{ 'background-image': 'url(' + member.user.avatar + ')'}"></div>
          <div v-if="seenMoreNumber" class="plus-more member is-flex" :class="{'plus-more-edit': editMode, 'three-digits': membersCount.toString().length > 2}">+{{membersCount - 4}}</div>
        </div>
      </div>
    </div>
    <!-- Dropdown -->
    <div v-if="showDropdown && !editMode" class="dropdown" @mouseover="mouseOnDropdown = true" @mouseleave="mouseOnDropdown = false">
      <div class="dropdown-content">
        <div class="top is-flex">
          <span class="subtitle is-4 is-marginless">{{ $t('projectPage.team') }}</span>
          <div class="is-flex cursor-pointer">
            <div class="edit-btn has-icon cursor-pointer is-uppercase" @click="edit" v-if="(canEdit && members.length > 1) || isJudgeTeamProject">
              <span v-if="!isJudgeTeamProject"><i class="fal fa-pencil-alt"></i>&nbsp;&nbsp;{{ $t("edit") }}</span>
              <span v-if="isJudgeTeamProject">
                <span v-if="currentUser.isJudgingAdmin"><i class="fal fa-pencil-alt"></i> Edit Groups</span>
                <span v-if="!currentUser.isJudgingAdmin"><i class="fal fa-eye"></i> View Groups</span>
              </span>
            </div>
            <div class="close is-hidden-desktop" @click="showDropdown = false"><span class="icon"><i class="fal fa-times"></i></span></div>
          </div>
        </div>
        <div class="members-wrapper cursor-pointer">
          <div class="members is-flex">
            <div v-if="loading && members.length === 0" class="loader is-centered"></div>
            <infinite-scroll :callback="fetchMembers">
              <div v-for="member in members" :key="member.user.userName" class="member is-flex" @click="view(member.user)">
                <div class="member-img" v-bind:style="{ 'background-image': 'url(' + member.user.avatar + ')'}"></div>
                <div class="is-flex member-text">
                  <div class="member-name subtitle is-6 is-marginless">{{member.user.userName}}
                    <span v-if="member.user.isEngStaff" class="staff-shield"><i class="fal fa-shield-check"></i></span>
                  </div>
                  <div class="member-role subtitle is-7 is-marginless">{{userRole(member)}}</div>
                </div>
              </div>
            </infinite-scroll>
            <div v-if="loading && hasMore" class="loader is-centered"></div>
          </div>
        </div>
      </div>
    </div>
    <edit-modal ref="editModal" v-bind="{loading, hasMore, addCallback, removeCallback, updateCallback, members, project}" @close="$emit('close')" @fetchMembers="fetchMembers" @getMembersCount="getMembersCount" @hasMembersChanged="hasMembersChanged"></edit-modal>
  </div>
</template>

<script>
import state from '../../state'
import verge from 'verge'
import EditModal from './EditMembersModal'
import UserList from '@/components/Controls/UserList'
import InfiniteScroll from '@/components/Controls/InfiniteScroll'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'

export default {
  components: {
    UserList,
    EditModal,
    InfiniteScroll
  },
  props: ['project', 'editMode', 'avatar'],
  mixins: [auth, componentState],
  data: function () {
    return {
      members: [],
      hasMore: false,
      fetchID: 0,
      limit: 20,
      offset: 0,
      loading: false,
      membership: null,
      showDropdown: false,
      mouseOnDropdown: false
    }
  },
  computed: {
    canEdit: function () {
      return this._get(state.project.permissions.edit, false)
    },
    nonOwners: function () {
      const mobile = verge.viewportW() < 475
      const userOwner = this.project.user?.userName
      const list = this.members.filter(function (u) {
        return u.role !== 'owner' && u.user.userName !== userOwner
      })
      return ((this.editMode || this.project.isPrivate) && !mobile) ? list : list.splice(0, 3)
    },
    seenMoreNumber: function () {
      const mobile = verge.viewportW() < 475
      return (this.members && this.members.length > 4) && ((!this.project.isPrivate && !this.editMode) || mobile)
    },
    isJudgeTeamProject: function () {
      return this.project.displayType === 'judgeTeam'
    },
    membersCount: function () {
      return this.$store.getters.projectMembersCount(this.project.id)
    }
  },
  mounted: function () {
    this.$store.dispatch('getMembersCount', this.project).then(() => {
      this.fetchMembers(true)
    })
  },
  watch: {
    project: function () {
      this.fetchMembers(true)
    },
    nonOwners: function (members) {
      this.$emit('createOrEdit', members?.length > 0)
    },
    membersCount: function () {
      this.$emit('showOwners')
    }
  },
  methods: {
    fetchMembers: function (reset = false) {
      if (!this.project || !this.project.id) {
        return
      }
      if (this.members.length === this.membersCount) {
        return
      }

      this.fetchID = this.project.id
      if (reset === true) {
        this.members = []
        this.offset = 0
        this.hasMore = false
      } else {
        this.offset += this.limit
      }
      this.loading = true
      this.$store.dispatch('getMembers', { project: this.project, limit: this.limit, offset: this.offset }).then((response) => {
        this.hasMore = response?.hasMore
        const members = []
        if (response && response.members) {
          Object.keys(response.members).forEach((uuid) => {
            if (response.members[uuid].user.userName !== '') {
              members.push(response.members[uuid])
            }
          })
        }

        if (this.currentUser !== this.project.user.userName) {
          members.forEach((m, i) => {
            if (m.user.id === this.currentUser.id) {
              const tempMember = members[0]
              members[0] = members[i]
              members[i] = tempMember
            }
          })

          this.members = members
        } else {
          this.offset -= this.limit
        }

        this.loading = false
      }, () => {
        this.loading = false
      })
    },
    getMembersCount: function () {
      return this.membersCount
    },
    hasMembersChanged: function (hasChanged) {
      this.$emit('hasMembersChanged', hasChanged)
    },
    edit: function () {
      this.$refs.editModal.show()
    },
    addCallback: function (member) {
      if (this.members.filter((m) => { return m.user.uuid === member.user.uuid }).length === 0) {
        this.members.push(member)
      }
    },
    updateCallback: function (member) {
      this.members.forEach((m, i) => {
        if (m.user.uuid === member.user.uuid) {
          this.$set(this.members, i, member)
        }
      })
    },
    removeCallback: function (member) {
      this.members = this.members.filter((m) => {
        return m.user.uuid !== member.user.uuid
      })
    },
    view: function (user) {
      this.$router.push({ name: 'profile', params: { username: user.userName } })
    },
    pauseOut: function () {
      setTimeout(() => {
        if (!this.mouseOnDropdown) {
          this.showDropdown = false
        }
      }, 250)
    },
    userRole: function (member) {
      if (member.customRole) {
        return member.customRole
      }
      return member.role
    },
    toggleDropdown: function () {
      this.showDropdown = !this.$refs.editModal.active
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';
  .fa-plus-circle {
    font-size: 1.25rem;
    margin-right: .5rem;
  }
  .add-member-icon {
    color: $primary;
    margin-right: .25rem;
    font-size: 1.5rem;
  }
  .centered {
    flex-direction: column;
    justify-content: center;
  }
  .edit {
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    align-content: center;
    color: $grey-dark;
    .edit:hover {
      color: $primary;
    }
  }
  .dropdown {
    top: 3rem;
    left: 0;
    position: absolute;
    z-index: 10;
    min-width: 300px;
    @include mobile {
      display: flex;
      justify-content: center;
      width: 100%;
      top: 3rem;
      min-width: 250px;

    }
    .dropdown-content {
      padding: 1rem;
    }
    .members-wrapper {
      max-height: 50vh;
      overflow-y: auto;
    }
    .members {
      flex-direction: column;
      flex-wrap: wrap;
      padding: 1rem 1rem 2rem;
      .member {
        margin: 0.5rem;
        align-items: center;
      }
      .member-text {
        flex-direction: column;
        margin-left: 0.5rem;
      }
      .member-role {
        padding-top: 0.25rem;
      }
      .member-img {
        height: 65px;
        width: 65px;
        border-radius: 50%;
        background-size: cover;
        background-position: center;
      }
    }
    .seeAllButton {
      border: none;
      background: none;
      width: 100%;
      text-align: center;
      cursor: pointer;
      font-size: 0.8rem;
      text-decoration: underline;
      margin-top: 1rem;
    }
    .top {
      padding-left: 0.5rem;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      .add-btn, .edit-btn, .close{
        margin-left: 1rem;
        font-size: 0.9rem;
      }
      .edit-btn:hover {
        color: $primary
      }
    }
  }
  .team-static-container {
    display: flex;
    align-items: center;
  }
  .team-static {
    flex-wrap: wrap;
    .team-btn {
      color: $white;
      margin: 0;
      margin-right: 0.5rem;
      height: 2rem;
      width: 2rem;
      border-radius: 50%;
      justify-content: center;
      align-items: center;
      position: relative;
      .team-btn-text {
        position: absolute;
        top: 2.25rem;
        color: $dark;
        font-size: 0.75rem;
      }
      .team-btn-text-private {
        display: none;
      }
    }
    .has-team {
      margin-left: 0.25rem;
    }
    .member {
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;
      height: 1.5rem;
      width: 1.5rem;
      border-radius: 50%;
      margin-right: 0.25rem;
      margin-bottom: 0.25rem;
    }
  }
  .plus-more {
    background-color: $dark;
    font-size: 0.7rem;
    font-weight: bold;
    color: $white;
    align-items: center;
    justify-content: center;
    &.three-digits {
      font-size: 0.55rem;
    }
  }
  .plus-more.plus-more-edit {
    height: 2rem;
    width: 2rem;
  }
  .team-private-edit {
    display: flex;
    flex-direction: row;
  }
  .meta-title {
    pointer-events: none;
    cursor: default;
  }
  .meta-title-private {
    display: flex;
    margin-right: 0.5rem;
    align-items: center;
    font-size: 0.7rem
  }
  .all-team {
    flex-wrap: wrap;
  }
  .team-wrapper {
    position: relative;
    flex-wrap: wrap;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    @include mobile {
      position: static;
    }
  }
  .meta-title-edit {
    cursor: pointer;
    pointer-events: all;
  }
  .loader.is-centered {
    font-size: 1.5rem;
    margin: 1rem auto;
  }
</style>
